import { useEffect, useMemo } from 'react'

import { useAppSelector } from 'store'
import { getMapComponent } from 'utils'
import { TUseUnityHubCreateChannel, useUnityHubCreateChannel } from 'App/components/UnityHub'
import { TAccountsSelectOptionData } from 'App/components/common/AccountsSelect/AccountsSelectOption'

import { MembersStep, TMembersStepProps } from '../MembersStep'
import { useMembersRequest } from '../useMembersRequest'
import { useChatDepartmentsRequest } from '../useChatDepartmentsRequest'

import {
  CreateTextChannelDialogMainStep,
  TCreateTextChannelDialogMainStepProps
} from './CreateTextChannelDialogMainStep'

enum steps {
  MAIN = 0,
  MEMBERS = 1,
  CHANEL_MANAGER = 2
}

const MAX_SELECTED_CHANNEL_MANAGER = 5

type TStepsMapProps = Pick<
  TCreateTextChannelDialogMainStepProps,
  | 'channelId'
  | 'setName'
  | 'channelMembers'
  | 'channelManagers'
  | 'channelDepartments'
  | 'isEdit'
  | 'setIsPublic'
  | 'loading'
  | 'onEditSuccess'
  | 'dirty'
  | 'initialValues'
> &
  Pick<
    TMembersStepProps,
    | 'setChannelMembers'
    | 'setChannelManagers'
    | 'setChannelDepartments'
    | 'isLeaderInDepartments'
    | 'organizationId'
  > & {
    name: string
    isPublic: boolean
    handleOpenMain: () => void
    handleOpenChanelManager: () => void
    handleOpenMembers: () => void
  }

const STEPS_MAP = new Map<steps, (props: TStepsMapProps) => JSX.Element>()
  .set(
    steps.MAIN,
    ({
      name,
      organizationId,
      channelId,
      channelMembers,
      channelManagers,
      isEdit,
      isLeaderInDepartments,
      loading,
      dirty,
      isPublic,
      channelDepartments,
      handleOpenChanelManager,
      handleOpenMembers,
      setName,
      setIsPublic,
      onEditSuccess,
      initialValues
    }) => (
      <CreateTextChannelDialogMainStep
        dirty={dirty}
        organizationId={organizationId}
        name={name}
        channelId={channelId}
        channelMembers={channelMembers}
        channelManagers={channelManagers}
        channelDepartments={channelDepartments}
        isEdit={isEdit}
        isPublic={isPublic}
        isLeaderInDepartments={isLeaderInDepartments}
        loading={loading}
        setName={setName}
        onOpenChanelManager={handleOpenChanelManager}
        onOpenMembers={handleOpenMembers}
        setIsPublic={setIsPublic}
        onEditSuccess={onEditSuccess}
        initialValues={initialValues}
      />
    )
  )
  .set(
    steps.MEMBERS,
    ({
      channelMembers,
      channelDepartments,
      organizationId,
      isLeaderInDepartments,
      channelManagers,
      channelId,
      handleOpenMain,
      setChannelMembers,
      setChannelManagers,
      setChannelDepartments
    }) => (
      <MembersStep
        variant="members"
        channelManagerIds={channelManagers?.map((manager) => manager.id)}
        isLeaderInDepartments={isLeaderInDepartments}
        organizationId={organizationId}
        channelMembers={channelMembers}
        channelDepartments={channelDepartments}
        markChatIds={channelId ? [channelId] : undefined}
        onBack={handleOpenMain}
        setChannelMembers={setChannelMembers}
        setChannelManagers={setChannelManagers}
        setChannelDepartments={setChannelDepartments}
      />
    )
  )
  .set(
    steps.CHANEL_MANAGER,
    ({
      organizationId,
      isLeaderInDepartments,
      channelManagers,
      setChannelManagers,
      setChannelMembers,
      handleOpenMain
    }) => (
      <MembersStep
        variant="manager"
        maxSelected={MAX_SELECTED_CHANNEL_MANAGER}
        channelManagerIds={channelManagers?.map((manager) => manager.id)}
        isLeaderInDepartments={isLeaderInDepartments}
        organizationId={organizationId}
        channelMembers={channelManagers ? channelManagers : []}
        onBack={handleOpenMain}
        setChannelMembers={setChannelMembers}
        setChannelManagers={setChannelManagers}
      />
    )
  )

export type TCreateTextChannelDialogProps = Pick<
  TUseUnityHubCreateChannel,
  'channelId' | 'channelName' | 'isPublic'
> &
  Pick<TCreateTextChannelDialogMainStepProps, 'onEditSuccess'> & {
    organizationId: string
  }

export const CreateTextChannelDialog = ({
  organizationId,
  channelName,
  onEditSuccess,
  channelId = '',
  isPublic: propIsPublic
}: TCreateTextChannelDialogProps) => {
  const {
    name,
    isPublic,
    channelMembers,
    channelManagers,
    channelDepartments,
    step,
    isEdit,
    dirty,
    handleOpenMain,
    handleOpenMembers,
    handleOpenChanelManager,
    setName,
    setIsPublic,
    setChannelMembers,
    setChannelManagers,
    setChannelDepartments
  } = useUnityHubCreateChannel({
    channelName,
    channelId,
    isPublic: propIsPublic,
    maxSelectedChannelManager: MAX_SELECTED_CHANNEL_MANAGER
  })

  const room = useAppSelector((state) => state.chat.chatRooms.list[channelId])
  const accountData = useAppSelector((state) => state.global.accountData)
  const isLeaderInDepartments = useAppSelector(
    (state) => state.organizations.data.list[organizationId]?.isLeaderInDepartments
  )

  const initialValues = useMemo(
    () => ({ name: channelName || '', isPublic: propIsPublic || false }),
    [channelName, propIsPublic]
  )

  const { isLoading, accounts } = useMembersRequest({
    variant: 'chat',
    channelId,
    organizationId,
    isEdit,
    setChannelMembers
  })

  const { isLoading: departmentsLoading } = useChatDepartmentsRequest({
    channelId,
    isEdit,
    setChannelDepartments
  })

  useEffect(() => {
    if (isEdit && accounts?.length && room?.channelManagers) {
      if (isLeaderInDepartments && accountData?.id) {
        setChannelMembers(
          accounts.filter((item) => item.id !== accountData.id),
          true
        )
      }

      setChannelManagers(room.channelManagers, true)
    }
  }, [
    accounts,
    room?.channelManagers,
    accountData?.id,
    isEdit,
    isLeaderInDepartments,
    organizationId,
    setChannelManagers,
    setChannelMembers
  ])

  useEffect(() => {
    if (isLeaderInDepartments && accountData) {
      const myAccount: TAccountsSelectOptionData = {
        id: accountData.id,
        type: accountData.type,
        displayUserName: accountData.displayUserName,
        firstName: accountData.firstName,
        lastName: accountData.lastName,
        disabled: true
      }

      setChannelManagers([myAccount], true)
    }
  }, [isLeaderInDepartments, accountData, setChannelManagers])

  return (
    <>
      {getMapComponent(STEPS_MAP, step, {
        name,
        isPublic,
        channelMembers,
        channelManagers,
        channelDepartments,
        channelId,
        isLeaderInDepartments,
        isEdit,
        organizationId,
        loading: isLoading || departmentsLoading,
        dirty,
        initialValues,
        setName,
        setIsPublic,
        setChannelMembers,
        setChannelManagers,
        setChannelDepartments,
        handleOpenMain,
        handleOpenMembers,
        handleOpenChanelManager,
        onEditSuccess
      })}
    </>
  )
}
